import { useState, useEffect } from 'react';
import { useAuth, canSeeCost, canSeeSellPrice, canSeeMargin, canEditPricing, canExport } from '../context/AuthContext';

const S = {
  page: { padding: '32px 36px', fontFamily: "'Inter', sans-serif" },
  topbar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  sub: { fontSize: 14, color: 'var(--text3)', marginTop: 4 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  btnPrimary: { background: 'var(--purple)', color: '#fff', border: '1px solid var(--purple)' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 },
  stat: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' },
  statLabel: { fontSize: 12, color: 'var(--text3)', margin: '0 0 6px', fontWeight: 500 },
  statVal: { fontSize: 26, fontWeight: 600, color: 'var(--text1)', margin: 0 },
  tableWrap: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' },
  td: { padding: '13px 16px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', verticalAlign: 'middle' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', fontSize: 13, color: 'var(--text2)', fontFamily: "'Inter', sans-serif" },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  editInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--purple)', background: 'var(--card)', fontSize: 13, width: 80, fontFamily: "'Inter', sans-serif" },
};

export default function ProductsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');

  useEffect(() => {
    fetch('/api/products')
      .then(r => r.json())
      .then(d => { setProducts(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.code?.toLowerCase().includes(search.toLowerCase())
  );

  const avgMargin = () => {
    const withPrices = products.filter(p => p.default_sell_price > 0 && p.cost_price > 0);
    if (!withPrices.length) return 0;
    const total = withPrices.reduce((s, p) => s + ((p.default_sell_price - p.cost_price) / p.default_sell_price * 100), 0);
    return total / withPrices.length;
  };

  const handleSavePrice = async (product) => {
    const newPrice = parseFloat(editVal);
    if (isNaN(newPrice)) return;
    await fetch(`/api/products/${product.id}/price`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ default_sell_price: newPrice }),
    });
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, default_sell_price: newPrice } : p));
    setEditingId(null);
  };

  const handleExport = () => window.open('/api/products/export', '_blank');

  if (loading) return <div style={{ ...S.page, color: 'var(--text3)' }}>Loading products...</div>;

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div>
          <h1 style={S.title}>Products</h1>
          <p style={S.sub}>Microsoft cloud products synced from LeaderCloud</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canExport(user) && <button style={S.btn} onClick={handleExport}>⤓ Export CSV</button>}
          {canEditPricing(user) && <button style={{ ...S.btn, ...S.btnPrimary }}>+ Add product</button>}
        </div>
      </div>

      <div style={S.stats}>
        <div style={S.stat}>
          <p style={S.statLabel}>Total products</p>
          <p style={S.statVal}>{products.length}</p>
        </div>
        <div style={S.stat}>
          <p style={S.statLabel}>Active</p>
          <p style={{ ...S.statVal, color: 'var(--green)' }}>{products.filter(p => p.is_active).length}</p>
        </div>
        {canSeeMargin(user) && (
          <>
            <div style={S.stat}>
              <p style={S.statLabel}>Avg margin</p>
              <p style={{ ...S.statVal, color: 'var(--green)' }}>{avgMargin().toFixed(1)}%</p>
            </div>
            <div style={S.stat}>
              <p style={S.statLabel}>Below 10% margin</p>
              <p style={{ ...S.statVal, color: 'var(--orange)' }}>
                {products.filter(p => p.cost_price > 0 && p.default_sell_price > 0 && ((p.default_sell_price - p.cost_price) / p.default_sell_price * 100) < 10).length}
              </p>
            </div>
          </>
        )}
        {!canSeeMargin(user) && (
          <div style={S.stat}>
            <p style={S.statLabel}>Charge type</p>
            <p style={{ ...S.statVal, fontSize: 16, paddingTop: 6 }}>Prepaid</p>
          </div>
        )}
        {!canSeeMargin(user) && (
          <div style={S.stat}>
            <p style={S.statLabel}>Last synced</p>
            <p style={{ ...S.statVal, fontSize: 14, paddingTop: 6 }}>Today</p>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input style={{ ...S.input, width: 260 }} placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.input, width: 150 }}>
          <option>All types</option>
          <option>Prepaid</option>
          <option>Postpaid</option>
        </select>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Product name</th>
              <th style={S.th}>Code</th>
              <th style={S.th}>Type</th>
              {canSeeCost(user)      && <th style={{ ...S.th, textAlign: 'right' }}>Cost price</th>}
              {canSeeSellPrice(user) && <th style={{ ...S.th, textAlign: 'right' }}>Sell price</th>}
              {canSeeMargin(user)    && <th style={{ ...S.th, textAlign: 'right' }}>Margin</th>}
              <th style={S.th}>Status</th>
              {canEditPricing(user)  && <th style={S.th}></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const margin = p.default_sell_price > 0 && p.cost_price > 0
                ? ((p.default_sell_price - p.cost_price) / p.default_sell_price * 100)
                : null;
              const lowMargin = margin !== null && margin < 10;
              return (
                <tr key={p.id} style={lowMargin && canSeeMargin(user) ? { background: 'var(--obg)' } : {}}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 13 }}>{p.name}</div>
                    {lowMargin && canSeeMargin(user) && (
                      <span style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 600 }}>⚠ Low margin</span>
                    )}
                  </td>
                  <td style={{ ...S.td, color: 'var(--text3)', fontSize: 11, fontFamily: 'monospace' }}>{p.code?.substring(0, 20)}...</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: 'var(--gbg)', color: 'var(--green)' }}>{p.charge_type || 'Prepaid'}</span>
                  </td>
                  {canSeeCost(user) && (
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>
                      {p.cost_price ? `$${parseFloat(p.cost_price).toFixed(2)}` : '—'}
                    </td>
                  )}
                  {canSeeSellPrice(user) && (
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {editingId === p.id && canEditPricing(user) ? (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <input style={S.editInput} type="number" value={editVal} onChange={e => setEditVal(e.target.value)} autoFocus />
                          <button style={{ ...S.btn, padding: '4px 8px', fontSize: 12 }} onClick={() => handleSavePrice(p)}>✓</button>
                          <button style={{ ...S.btn, padding: '4px 8px', fontSize: 12 }} onClick={() => setEditingId(null)}>✕</button>
                        </div>
                      ) : (
                        <span style={{ fontWeight: 500 }}>
                          {p.default_sell_price ? `$${parseFloat(p.default_sell_price).toFixed(2)}` : '—'}
                        </span>
                      )}
                    </td>
                  )}
                  {canSeeMargin(user) && (
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: margin === null ? 'var(--text3)' : margin >= 20 ? 'var(--green)' : margin >= 10 ? 'var(--orange)' : 'var(--red)' }}>
                      {margin !== null ? `${margin.toFixed(1)}%` : '—'}
                    </td>
                  )}
                  <td style={S.td}>
                    <span style={{ ...S.badge, ...(p.is_active ? { background: 'var(--gbg)', color: 'var(--green)' } : { background: 'var(--bg3)', color: 'var(--text3)' }) }}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canEditPricing(user) && (
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={{ ...S.btn, padding: '5px 10px', fontSize: 12 }}
                        onClick={() => { setEditingId(p.id); setEditVal(p.default_sell_price || ''); }}>
                        ✎ Edit price
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: 'var(--text3)', padding: 32 }}>No products found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
        {filtered.length} of {products.length} products shown
        {canSeeMargin(user) && ' · Low margin highlighted in amber'}
        {canEditPricing(user) && ' · Click "Edit price" to update sell price'}
      </p>
    </div>
  );
}
